import { useState } from "react";
import PageHeader from "@/components/PageHeader";
import DataTable from "@/components/DataTable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScanLine, QrCode, Barcode, Radio, Search, PackageCheck, XCircle } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const TYPES = [
  { key: "qr", label: "QR Code", icon: QrCode },
  { key: "barcode", label: "Barcode", icon: Barcode },
  { key: "rfid", label: "RFID", icon: Radio },
];

export default function Scan() {
  const [scanType, setScanType] = useState("qr");
  const [code, setCode] = useState("");
  const [result, setResult] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(false);
  const [refresh, setRefresh] = useState(0);

  const doScan = async () => {
    if (!code.trim()) return;
    setLoading(true); setNotFound(false); setResult(null);
    try {
      const { data } = await api.post("/scan", { code: code.trim(), scan_type: scanType, mode: "lookup" });
      setResult(data);
      toast.success(`Found: ${data.item.name}`);
    } catch (e) {
      setNotFound(true);
      toast.error(e.response?.data?.detail || "No item found");
    } finally { setLoading(false); setRefresh((r) => r + 1); }
  };

  const cols = [
    { key: "timestamp", label: "Time", render: (r) => new Date(r.timestamp).toLocaleString() },
    { key: "scan_type", label: "Type", render: (r) => <span className="uppercase text-xs font-semibold">{r.scan_type}</span> },
    { key: "code", label: "Code", mono: true },
    { key: "item_name", label: "Item", render: (r) => r.item_name || "—" },
    { key: "found", label: "Result", render: (r) => r.found ? <span className="text-emerald-600 text-sm">Matched</span> : <span className="text-red-500 text-sm">Not found</span> },
    { key: "user_name", label: "By" },
  ];

  return (
    <div>
      <PageHeader title="Scan Station" subtitle="QR · Barcode · RFID lookup and audit trail" icon={ScanLine}
        breadcrumb={["Home", "Operations", "Scan"]} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* scanner */}
        <div className="bg-white dark:bg-card border border-slate-200 dark:border-border rounded-lg p-6 shadow-sm">
          <div className="flex gap-2 mb-5">
            {TYPES.map((t) => (
              <button key={t.key} data-testid={`scan-type-${t.key}`} onClick={() => setScanType(t.key)}
                className={`flex-1 flex flex-col items-center gap-1.5 py-3 rounded-lg border-2 transition-colors ${
                  scanType === t.key ? "border-blue-600 bg-blue-50 text-blue-700 dark:bg-blue-500/10" : "border-slate-200 dark:border-border text-slate-500 hover:bg-slate-50"
                }`}>
                <t.icon className="h-6 w-6" />
                <span className="text-xs font-semibold">{t.label}</span>
              </button>
            ))}
          </div>

          <div className="relative bg-slate-900 rounded-lg overflow-hidden border-2 border-slate-800 h-48 mb-5 flex items-center justify-center">
            <div className="absolute inset-0 border-[3px] border-green-500/40 m-8 rounded" />
            <div className="absolute left-8 right-8 h-0.5 bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.8)] animate-scan" />
            <ScanLine className="h-12 w-12 text-slate-600" />
          </div>

          <div className="flex gap-2">
            <Input data-testid="scan-input" value={code} onChange={(e) => setCode(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && doScan()}
              placeholder="Enter or scan code (e.g. QR-SKU-1000, SKU-1001)" className="h-12 font-mono" />
            <Button data-testid="scan-submit" onClick={doScan} disabled={loading} className="h-12 px-6 bg-blue-600 hover:bg-blue-700">
              <Search className="h-5 w-5 mr-1.5" />Lookup
            </Button>
          </div>
          <p className="text-xs text-slate-400 mt-2">Try: <span className="font-mono">QR-SKU-1000</span>, <span className="font-mono">SKU-1005</span>, or an RFID tag.</p>
        </div>

        {/* result */}
        <div className="bg-white dark:bg-card border border-slate-200 dark:border-border rounded-lg p-6 shadow-sm">
          <h3 className="font-display font-semibold text-slate-800 dark:text-foreground mb-4">Scan Result</h3>
          {!result && !notFound && <div className="text-slate-400 text-sm py-12 text-center">Scan a code to see item details.</div>}
          {notFound && (
            <div className="flex flex-col items-center gap-3 py-10 text-red-500">
              <XCircle className="h-12 w-12" /><span className="font-medium">No matching item found</span>
            </div>
          )}
          {result && (
            <div data-testid="scan-result" className="fade-up">
              <div className="flex items-center gap-3 mb-4">
                <div className="h-12 w-12 rounded-lg bg-emerald-50 flex items-center justify-center"><PackageCheck className="h-6 w-6 text-emerald-600" /></div>
                <div>
                  <div className="font-display font-bold text-lg text-slate-900 dark:text-foreground">{result.item.name}</div>
                  <div className="font-mono text-sm text-slate-500">{result.item.sku}</div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                {[["Category", result.item.category], ["Brand", result.item.brand], ["Barcode", result.item.barcode], ["Total Stock", result.item.stock_qty]].map(([l, v]) => (
                  <div key={l} className="p-3 rounded-md bg-slate-50 dark:bg-secondary">
                    <div className="text-xs text-slate-400">{l}</div>
                    <div className="font-medium text-slate-800 dark:text-foreground font-mono">{v ?? "—"}</div>
                  </div>
                ))}
              </div>
              <div className="mt-4">
                <div className="text-xs font-semibold text-slate-500 uppercase mb-2">Stock by warehouse</div>
                {result.stock.map((s, i) => (
                  <div key={i} className="flex justify-between py-1.5 border-b border-slate-50 dark:border-border text-sm">
                    <span className="font-mono text-slate-500">{s.warehouse_id.slice(0, 8)}</span>
                    <span className="font-semibold">{s.quantity} units</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <h3 className="font-display font-semibold text-slate-800 dark:text-foreground mb-3">Scan History (Audit Trail)</h3>
      <DataTable endpoint="/scan/history" columns={cols} testId="scan-history-table" searchable={false} refreshKey={refresh} />
    </div>
  );
}
